import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';

const categories = {
    news: { name: 'お知らせ', color: 'bg-gray-100 text-gray-800' },
    maintenance: { name: 'メンテナンス', color: 'bg-yellow-100 text-yellow-800' },
    incident: { name: '障害情報', color: 'bg-red-100 text-red-800' },
    release: { name: '機能追加', color: 'bg-blue-100 text-blue-800' },
    important: { name: '重要', color: 'bg-red-100 text-red-800' },
};

const CategoryBadge = ({ category }) => {
    const categoryInfo = categories[category] || categories['news'];
    return (
        <span className={`px-2 py-1 mr-3 inline-flex text-xs leading-5 font-semibold rounded-md ${categoryInfo.color}`}>
            {categoryInfo.name}
        </span>
    );
};

const AnnouncementListPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);

    const announcementsRef = collection(db, 'announcements');
    const announcementsLimit = 20;

    const fetchAnnouncements = useCallback(async (loadMore = false) => {
        setLoading(true);
        setError(null);
        try {
            let q;
            if (loadMore && lastVisible) {
                q = query(
                    announcementsRef,
                    where('status', '==', 'published'),
                    orderBy('publishedAt', 'desc'),
                    startAfter(lastVisible),
                    limit(announcementsLimit)
                );
            } else {
                q = query(
                    announcementsRef,
                    where('status', '==', 'published'),
                    orderBy('publishedAt', 'desc'),
                    limit(announcementsLimit)
                );
            }

            const documentSnapshots = await getDocs(q);

            if (documentSnapshots.empty) {
                setHasMore(false);
                setLoading(false);
                return;
            }

            const newAnnouncements = documentSnapshots.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const lastDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];
            setLastVisible(lastDoc);

            setAnnouncements(prev => loadMore ? [...prev, ...newAnnouncements] : newAnnouncements);
            if (documentSnapshots.docs.length < announcementsLimit) {
                setHasMore(false);
            }

        } catch (err) {
            console.error("Error fetching announcements:", err);
            setError('お知らせの読み込みに失敗しました。');
        } finally {
            setLoading(false);
        }
    }, [lastVisible]);

    useEffect(() => {
        fetchAnnouncements();
    }, []); // Initial fetch

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString('ja-JP');
    };

    return (
        <div className="bg-white py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h1 className="text-3xl font-bold text-gray-900">
                        お知らせ一覧
                    </h1>
                </div>

                {error && <div className="text-red-500 text-center mb-6">{error}</div>}

                {loading && announcements.length === 0 ? (
                    <p className="text-center">読み込み中...</p>
                ) : announcements.length > 0 ? (
                    <div className="space-y-2">
                        {announcements.map((item) => (
                            <details key={item.id} className="p-4 border-b border-gray-200 group">
                                <summary className="flex items-center justify-between cursor-pointer list-none">
                                    <div className="flex items-center">
                                        <p className="text-sm text-gray-500 mr-4 flex-shrink-0">
                                            {formatDate(item.publishedAt)}
                                        </p>
                                        <div className="flex items-center">
                                          <CategoryBadge category={item.category} />
                                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600">
                                              {item.title}
                                          </h3>
                                        </div>
                                    </div>
                                    <div className="transition-transform duration-300 group-open:rotate-90">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-500"><path d="m9 18 6-6-6-6"/></svg>
                                    </div>
                                </summary>
                                <div className="mt-4 pl-8 text-gray-700">
                                    <p style={{ whiteSpace: 'pre-wrap' }}>{item.content}</p>
                                </div>
                            </details>
                        ))}

                        {hasMore && (
                             <div className="text-center pt-8">
                                <button
                                    onClick={() => fetchAnnouncements(true)}
                                    disabled={loading}
                                    className="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                                >
                                    {loading ? '読み込み中...' : 'さらに表示'}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-center text-gray-500">公開されているお知らせはありません。</p>
                )}
                 <div className="mt-12 text-center">
                    <Link
                        to="/"
                        className="inline-block bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                    >
                        トップページへ戻る
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementListPage;
